import {
  MAX_REALTIME_FRAME_BYTES,
  REALTIME_HEARTBEAT_INTERVAL_MS,
  REALTIME_PROTOCOL_VERSION,
  REALTIME_STALE_AFTER_MS,
  REALTIME_TICKET_SUBPROTOCOL_PREFIX,
  RealtimeEventSchema,
  type RealtimeEvent,
  type RealtimeTicketResponse,
  type RealtimeTransportState,
} from '@deffy-direct/contracts';

const MAX_RECONNECT_DELAY_MS = 30_000;
const TYPING_IDLE_MS = 2_000;
const TYPING_THROTTLE_MS = 500;

export function realtimeSocketUrl(
  apiBaseUrl: string,
  conversationPublicId: string,
  cursor: number,
): string {
  const url = new URL(
    `${apiBaseUrl.replace(/\/$/u, '')}/realtime/conversations/${encodeURIComponent(conversationPublicId)}`,
  );
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  if (cursor > 0) url.searchParams.set('cursor', String(cursor));
  return url.toString();
}

export function reconnectDelay(attempt: number, random = Math.random): number {
  const exponent = Math.min(Math.max(0, attempt), 5);
  const base = Math.min(MAX_RECONNECT_DELAY_MS, 1_000 * 2 ** exponent);
  return Math.round(base * (0.8 + random() * 0.4));
}

export function eventAdvancesCursor(type: RealtimeEvent['type']): boolean {
  return ![
    'connection.ready',
    'typing.started',
    'typing.stopped',
    'heartbeat.pong',
    'red_line.typing',
    'red_line.presence',
  ].includes(type);
}

interface RealtimeClientOptions {
  apiBaseUrl: string;
  conversationPublicId: string;
  ticketProvider: () => Promise<RealtimeTicketResponse>;
  onEvent: (event: RealtimeEvent) => void;
  onState: (state: RealtimeTransportState) => void;
  webSocketFactory?: (url: string, protocols: string[]) => WebSocket;
}

export class ConversationRealtimeClient {
  readonly #options: RealtimeClientOptions;
  readonly #seenEventIds = new Set<string>();
  #socket: WebSocket | null = null;
  #state: RealtimeTransportState = 'idle';
  #running = false;
  #attempt = 0;
  #cursor = 0;
  #lastActivityAt = 0;
  #lastTypingSentAt = 0;
  #connectionGeneration = 0;
  #reconnectTimer: number | null = null;
  #heartbeatTimer: number | null = null;
  #watchdogTimer: number | null = null;
  #typingTimer: number | null = null;

  constructor(options: RealtimeClientOptions) {
    this.#options = options;
    this.#cursor = this.#storedCursor();
  }

  start(): void {
    if (this.#running) return;
    this.#running = true;
    window.addEventListener('online', this.#handleOnline);
    document.addEventListener('visibilitychange', this.#handleVisibility);
    void this.#connect();
  }

  stop(): void {
    if (!this.#running) return;
    this.#running = false;
    this.#connectionGeneration += 1;
    window.removeEventListener('online', this.#handleOnline);
    document.removeEventListener('visibilitychange', this.#handleVisibility);
    this.#clearTimers();
    this.#sendTypingFrame(false);
    this.#socket?.close(1000, 'client stopped');
    this.#socket = null;
    this.#setState('idle');
  }

  setTyping(active: boolean): void {
    if (!active) {
      if (this.#typingTimer !== null) window.clearTimeout(this.#typingTimer);
      this.#typingTimer = null;
      this.#sendTypingFrame(false);
      return;
    }

    const now = Date.now();
    if (now - this.#lastTypingSentAt >= TYPING_THROTTLE_MS) {
      this.#sendTypingFrame(true);
      this.#lastTypingSentAt = now;
    }
    if (this.#typingTimer !== null) window.clearTimeout(this.#typingTimer);
    this.#typingTimer = window.setTimeout(() => {
      this.#typingTimer = null;
      this.#sendTypingFrame(false);
    }, TYPING_IDLE_MS);
  }

  readonly #handleOnline = (): void => {
    if (this.#running && !this.#socket) void this.#connect();
  };

  readonly #handleVisibility = (): void => {
    if (!this.#running || document.visibilityState !== 'visible') return;
    if (
      this.#socket &&
      this.#lastActivityAt > 0 &&
      Date.now() - this.#lastActivityAt > REALTIME_STALE_AFTER_MS
    ) {
      this.#socket.close(4001, 'connection stale after resume');
      return;
    }
    if (!this.#socket) void this.#connect();
  };

  async #connect(): Promise<void> {
    if (!this.#running || this.#socket || navigator.onLine === false) {
      if (navigator.onLine === false) this.#scheduleReconnect();
      return;
    }

    const generation = ++this.#connectionGeneration;
    this.#setState('connecting');
    try {
      const ticket = await this.#options.ticketProvider();
      if (!this.#running || generation !== this.#connectionGeneration) return;
      const createSocket =
        this.#options.webSocketFactory ??
        ((url: string, protocols: string[]) => new WebSocket(url, protocols));
      const socket = createSocket(
        realtimeSocketUrl(
          this.#options.apiBaseUrl,
          this.#options.conversationPublicId,
          this.#cursor,
        ),
        [ticket.protocol, `${REALTIME_TICKET_SUBPROTOCOL_PREFIX}${ticket.ticket}`],
      );
      this.#socket = socket;
      socket.addEventListener('open', () => this.#opened(socket));
      socket.addEventListener('message', (event) => this.#message(socket, event));
      socket.addEventListener('close', () => this.#closed(socket));
      socket.addEventListener('error', () => {
        if (socket.readyState < WebSocket.CLOSING) socket.close();
      });
    } catch {
      if (generation === this.#connectionGeneration) this.#scheduleReconnect();
    }
  }

  #opened(socket: WebSocket): void {
    if (socket !== this.#socket) return;
    this.#lastActivityAt = Date.now();
    this.#heartbeatTimer = window.setInterval(() => {
      this.#send({
        version: REALTIME_PROTOCOL_VERSION,
        type: 'heartbeat.ping',
        clientTimestamp: new Date().toISOString(),
      });
    }, REALTIME_HEARTBEAT_INTERVAL_MS);
    this.#watchdogTimer = window.setInterval(() => {
      if (Date.now() - this.#lastActivityAt > REALTIME_STALE_AFTER_MS) {
        socket.close(4001, 'heartbeat timeout');
      }
    }, 10_000);
  }

  #message(socket: WebSocket, message: MessageEvent<unknown>): void {
    if (socket !== this.#socket || typeof message.data !== 'string') return;
    if (new TextEncoder().encode(message.data).byteLength > MAX_REALTIME_FRAME_BYTES) {
      socket.close(1009, 'frame too large');
      return;
    }

    let value: unknown;
    try {
      value = JSON.parse(message.data) as unknown;
    } catch {
      socket.close(1003, 'invalid JSON');
      return;
    }
    const parsed = RealtimeEventSchema.safeParse(value);
    if (!parsed.success || parsed.data.conversationId !== this.#options.conversationPublicId) {
      socket.close(1003, 'invalid realtime event');
      return;
    }

    this.#lastActivityAt = Date.now();
    const event = parsed.data;
    if (this.#seenEventIds.has(event.eventId)) return;
    this.#rememberEvent(event.eventId);
    if (eventAdvancesCursor(event.type) && event.sequence > this.#cursor) {
      this.#cursor = event.sequence;
      this.#storeCursor();
      this.#send({
        version: REALTIME_PROTOCOL_VERSION,
        type: 'cursor.ack',
        sequence: this.#cursor,
      });
    }
    if (event.type === 'connection.ready') {
      this.#attempt = 0;
      this.#setState('realtime');
    }
    this.#options.onEvent(event);
  }

  #closed(socket: WebSocket): void {
    if (socket !== this.#socket) return;
    this.#socket = null;
    this.#clearConnectionTimers();
    this.#scheduleReconnect();
  }

  #scheduleReconnect(): void {
    if (!this.#running || this.#reconnectTimer !== null) return;
    this.#setState('fallback');
    const delay = reconnectDelay(this.#attempt);
    this.#attempt += 1;
    this.#reconnectTimer = window.setTimeout(() => {
      this.#reconnectTimer = null;
      void this.#connect();
    }, delay);
  }

  #sendTypingFrame(active: boolean): void {
    this.#send({
      version: REALTIME_PROTOCOL_VERSION,
      type: 'typing.set',
      active,
    });
  }

  #send(value: object): void {
    if (this.#socket?.readyState !== WebSocket.OPEN) return;
    this.#socket.send(JSON.stringify(value));
  }

  #setState(next: RealtimeTransportState): void {
    if (this.#state === next) return;
    this.#state = next;
    this.#options.onState(next);
  }

  #rememberEvent(eventId: string): void {
    this.#seenEventIds.add(eventId);
    if (this.#seenEventIds.size <= 256) return;
    const oldest = this.#seenEventIds.values().next().value;
    if (oldest) this.#seenEventIds.delete(oldest);
  }

  #cursorStorageKey(): string {
    return `deffy-direct:realtime:${this.#options.conversationPublicId}`;
  }

  #storedCursor(): number {
    try {
      const value = Number.parseInt(sessionStorage.getItem(this.#cursorStorageKey()) ?? '', 10);
      return Number.isSafeInteger(value) && value >= 0 ? value : 0;
    } catch {
      return 0;
    }
  }

  #storeCursor(): void {
    try {
      sessionStorage.setItem(this.#cursorStorageKey(), String(this.#cursor));
    } catch {
      // Cursor persistence is an optimization; HTTP polling remains authoritative.
    }
  }

  #clearConnectionTimers(): void {
    if (this.#heartbeatTimer !== null) window.clearInterval(this.#heartbeatTimer);
    if (this.#watchdogTimer !== null) window.clearInterval(this.#watchdogTimer);
    this.#heartbeatTimer = null;
    this.#watchdogTimer = null;
  }

  #clearTimers(): void {
    this.#clearConnectionTimers();
    if (this.#reconnectTimer !== null) window.clearTimeout(this.#reconnectTimer);
    if (this.#typingTimer !== null) window.clearTimeout(this.#typingTimer);
    this.#reconnectTimer = null;
    this.#typingTimer = null;
  }
}
